import {Alert, type FormControl} from 'platform/components';
import {useFormatCurrency} from 'platform/locale';

import {useWatch, type FieldValues, type Path} from 'react-hook-form';

import {t} from '../utils/catalogueUi';
import {negativeMargin} from '../utils/moneyInput';

/**
 * Non-blocking: a cost above the price still saves; the model year readiness check covers publishing.
 * Only for trims and variants, whose price is the vehicle's selling price. Option surcharges (colours,
 * equipment, packages) are often 0 with a real cost because they are included, so it would misfire there.
 */
export function NegativeMarginWarning<TFieldValues extends FieldValues>(props: {
  control: FormControl<TFieldValues>;
  priceName: Path<TFieldValues>;
  costName: Path<TFieldValues>;
  currency: string;
  /** The margin reveals the cost, so it is shown only with cost access. */
  canViewCost: boolean;
}) {
  const formatCurrency = useFormatCurrency();
  const [price, cost] = useWatch({control: props.control, name: [props.priceName, props.costName]});
  const margin = negativeMargin(price, cost);
  if (!props.canViewCost || margin === undefined) {
    return null;
  }
  return (
    <Alert
      variant="warning"
      message={t(
        'entity.newVehicle.catalogue.negativeMargin',
        'Cost is higher than price, margin {margin}.',
        {margin: formatCurrency(margin, props.currency, 2)}
      )}
      data-testid="newVehicleCatalogue-negativeMargin"
    />
  );
}
